import React from 'react';
import lottie from 'lottie-web';
import {reportBug} from '@packages/utilities/reporter';
import {isImmutable} from '@packages/utilities/imm4-backports';
import {
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_VIDEO_TYPES,
  MAX_IMAGE_ASSET_SIZE,
  MAX_DOCUMENT_ASSET_SIZE,
  MAX_VIDEO_ASSET_SIZE,
  MAGIC_BYTE_DETECTION_BYTE_LENGTH,
  COMPAT_THUMB_REGEX,
  COMPAT_DIMENSIONS_REGEX,
  SUPPORTED_AUDIO_TYPES,
  MAX_AUDIO_ASSET_SIZE,
  MAX_AUDIO_ASSET_SIZE_CANVAS,
  MAX_ZIP_ASSET_SIZE_CANVAS,
} from '@packages/shared/constants/Assets';

import {getNormalizedExtension} from './FileUtils';
import type {
  FileRecordProps,
  FileRecordType,
  RawFileType,
} from '@packages/systems/core/records/FileRecord';
import {weakMemo, once} from '@packages/utilities/memo';
import {AssetState, type AssetsType} from '@packages/systems/designer/assets';

import {assign, toUpper} from 'lodash';
import {List, Seq, Map} from 'immutable';
import {getMd5Hash} from '@packages/utilities/md5-browser';
import type {
  AssetManagerUIState,
  FolderT,
} from '@packages/systems/assetManager';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  ASSET_REPLACE_PERSIST_FAILED,
  ASSET_REPLACE_UPLOAD_FAILED,
  ASSET_S3_UPLOAD_NOT_NEEDED,
  ASSET_SIGNING_FAILED,
  ASSET_SIGNING_FINISHED,
  ASSET_UPLOAD_FAILED,
  ASSET_UPLOAD_FINISHED,
  FILE_HASHING_FAILED,
  FILE_READ_FOR_HASHING_FAILED,
  FILE_READ_FOR_VALIDATION_FAILED,
  FILE_READ_FOR_VALIDATION_FINISHED,
  IMAGE_ASSET_LOADING_FAILED,
  IMAGE_ASSET_THUMBNAIL_FAILED,
  IMAGE_READ_FOR_VALIDATION_FAILED,
  IMAGE_READ_FOR_VALIDATION_FINISHED,
  JSON_FILE_READ_FOR_VALIDATION_FAILED,
  JSON_FILE_READ_FOR_VALIDATION_FINISHED,
  RIVE_FILE_READ_FOR_VALIDATION_FAILED,
  RIVE_FILE_READ_FOR_VALIDATION_FINISHED,
  XSCP_ASSET_IMPORT_SUCCEEDED,
} from '@packages/systems/designer/actions';
import {
  strRightBack,
  strRight,
  prettySize,
} from '@packages/utilities/string-utils';
import {FileRecord} from '@packages/systems/core/records/FileRecord';
import {clamp, byteToHex} from '@packages/systems/core/utils/NumberUtils';
import {scaleToFit} from '@packages/utilities/box';
import {VARIANT_WIDTHS} from '@packages/systems/core/utils/SrcsetUtils';
import {
  ImageRefRecord,
  type ImageRefType,
} from '@packages/systems/core/records/ImageRefRecord';
import {FolderFilter, AssetFilterT} from '@packages/systems/assetManager';
import {isMintId} from '@packages/systems/core/utils/RecordUtils';
import {type FileHashingFinishedAction} from '@packages/systems/data-manager';
import {namesEqual} from '@packages/systems/wfdl-Qualified';
import {queryParam} from '@packages/utilities/environment';
import {
  ExcelDoc72Icon,
  GenericDoc72Icon,
  MusicNote72Icon,
  PDFDoc72Icon,
  PowerPointDoc72Icon,
  Rive72Icon,
  WordDoc72Icon,
  ZipFolder72Icon,
} from '@packages/systems/spring';
import {
  isImageAsset,
  isDocumentAsset,
  isVideoAsset,
  isAudioAsset,
  isLottieAsset,
  isRiveAsset,
  isFigmaAsset,
  isReadOnlyAsset,
  isLibraryAsset,
  isImageBlob as _isImageBlob,
  isVideoBlob as _isVideoBlob,
  isDocumentBlob as _isDocumentBlob,
  isJsonBlob as _isJsonBlob,
  isDotLottieBlob as _isDotLottieBlob,
  isDotRiveBlob as _isDotRiveBlob,
  isAudioBlob as _isAudioBlob,
  isZipBlob as _isZipBlob,
  isBlobOfType as _isBlobOfType,
} from '@packages/domains/assets/utilities';

type Dimensions = {
  width: number;
  height: number;
};

type stringToString = (arg1: string) => string;
type stringToBoolean = (arg1: string) => boolean;
type assetToBoolean = (arg1: FileRecordType) => boolean;
type numberToNumber = (arg1: number) => number;
type stringToNumber = (arg1: string, arg2: File) => number;
type blobToNumber = (arg1: File) => number;
type blobToString = (arg1: File) => string;
type assetToString = (arg1: FileRecordType) => string;
type assetToAsset = (arg1: FileRecordType) => FileRecordType;
type assetListToAssetList = (
  arg1: List<FileRecordType>
) => List<FileRecordType>;
type dimensionsToDimensions = (arg1: Dimensions) => Dimensions;

type RegexMap = {
  [key: string]: RegExp;
};

const isSiteAsset: assetToBoolean = (asset) => Boolean(asset.siteId);

const DOC_ICON_SIZES = {small: 24, medium: 48, large: 72} as const;

function createSizedIcon(Icon: React.ElementType, size: number) {
  return (props: Record<string, unknown>) =>
    React.createElement(Icon, {...props, size});
}

function createIconSet(Icon: React.ElementType) {
  return {
    small: createSizedIcon(Icon, DOC_ICON_SIZES.small),
    medium: createSizedIcon(Icon, DOC_ICON_SIZES.medium),
    large: createSizedIcon(Icon, DOC_ICON_SIZES.large),
  };
}

const DOC_ICON_SET = {
  generic: createIconSet(GenericDoc72Icon),
  pdf: createIconSet(PDFDoc72Icon),
  doc: createIconSet(WordDoc72Icon),
  docx: createIconSet(WordDoc72Icon),
  xls: createIconSet(ExcelDoc72Icon),
  xlsx: createIconSet(ExcelDoc72Icon),
  ppt: createIconSet(PowerPointDoc72Icon),
  pptx: createIconSet(PowerPointDoc72Icon),
  riv: createIconSet(Rive72Icon),
  zip: createIconSet(ZipFolder72Icon),
};

const AUDIO_ICON_SET = createIconSet(MusicNote72Icon);

type DocIcon = ReturnType<typeof createSizedIcon>;

export const getDocIcon = (
  url: string,
  size: 'small' | 'medium' | 'large',
  mimeType?: string
): DocIcon | null | undefined => {
  const getSupportedDocIcon = (extension: string) => {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly pdf: RegExp; readonly doc: RegExp; readonly xls: RegExp; readonly ppt: RegExp; readonly docx: RegExp; readonly xlsx: RegExp; ... 7 more ...; readonly lottie: RegExp; }'.
    if (SUPPORTED_DOCUMENT_TYPES[extension]) {
      const iconSet =
        // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type
        DOC_ICON_SET[extension] || DOC_ICON_SET.generic;

      return iconSet[size];
    }

    if (extension in SUPPORTED_AUDIO_TYPES) {
      return AUDIO_ICON_SET[size];
    }
  };
  const normalizedExtension = getNormalizedExtension(url);
  const iconFromUrl = getSupportedDocIcon(normalizedExtension);

  if (iconFromUrl) return iconFromUrl;

  if (mimeType) {
    const mimeTypeArr = mimeType.split('/');
    const fileExtension = mimeTypeArr.length === 2 && mimeTypeArr[1];
    if (!fileExtension) return;
    return getSupportedDocIcon(fileExtension);
  }
};

/**
 * Finds an existing asset that has the same file hash, and that has been uploaded in the same S3 bucket.
 */
export const findExistingIdenticalAsset = weakMemo((asset: FileRecordType) =>
  weakMemo((assets: AssetsType) => {
    // @ts-expect-error - TS2345 - Argument of type '(storedAsset: FileRecord | undefined) => boolean | "" | null' is not assignable to parameter of type '(value?: FileRecord | undefined, key?: string | undefined, iter?: Iterable<string, FileRecord> | undefined) => boolean'.
    return assets.find((storedAsset) => {
      const notDeleted = !storedAsset.markedAsDeleted;
      const isIdenticalFile = storedAsset.fileHash === asset.fileHash;
      const inSameSiteAssetBucket =
        storedAsset.siteId && storedAsset.siteId === asset.siteId;
      const inSameDatabaseAssetBucket =
        storedAsset.databaseId && storedAsset.databaseId === asset.databaseId;
      const inSameAssetBucket =
        inSameSiteAssetBucket || inSameDatabaseAssetBucket;

      return notDeleted && isIdenticalFile && inSameAssetBucket;
    });
  })
);

export const getPluginAsset =
  (assets: AssetsType) => (pluginName: string, assetName: string) => {
    const asset = assets.find(
      ({_id}) => _id === `plugins/${pluginName}/assets/${assetName}`
    );

    return asset ? {id: asset._id, url: asset.cdnUrl} : {id: '', url: ''};
  };

export const SIGN_PROGRESS = 0.2;

const supportedExtensions = assign(
  {},
  SUPPORTED_IMAGE_TYPES,
  SUPPORTED_DOCUMENT_TYPES,
  SUPPORTED_VIDEO_TYPES,
  SUPPORTED_AUDIO_TYPES
);

export const isSupportedExtension: stringToBoolean = (extension) =>
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly jpeg: RegExp; readonly jpg: RegExp; readonly png: RegExp; readonly gif: RegExp; readonly webp: RegExp; readonly bmp: RegExp; readonly svg: RegExp; } & { ...; } & { ...; }'.
  Boolean(supportedExtensions[extension]);

const kilobytes: numberToNumber = (kb) => kb * 1024;
const megabytes: numberToNumber = (mb) => kilobytes(mb) * 1024;

const isCMSWorkflow = () => queryParam('workflow') === 'cms';

const getMaxFileSize: stringToNumber = (type, blob) => {
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly jpeg: RegExp; readonly jpg: RegExp; readonly png: RegExp; readonly gif: RegExp; readonly webp: RegExp; readonly bmp: RegExp; readonly svg: RegExp; }'.
  if (SUPPORTED_IMAGE_TYPES[type]) {
    return megabytes(MAX_IMAGE_ASSET_SIZE);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly pdf: RegExp; readonly doc: RegExp; readonly xls: RegExp; readonly ppt: RegExp; readonly docx: RegExp; readonly xlsx: RegExp; ... 7 more ...; readonly lottie: RegExp; }'.
  } else if (SUPPORTED_DOCUMENT_TYPES[type]) {
    if (type === 'zip' && !isCMSWorkflow()) {
      return megabytes(MAX_ZIP_ASSET_SIZE_CANVAS);
    }
    return megabytes(MAX_DOCUMENT_ASSET_SIZE);
  } else if (
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly webm: RegExp; readonly mp4: RegExp; readonly mov: RegExp; readonly ogg: RegExp; }'.
    SUPPORTED_VIDEO_TYPES[type] &&
    blob.type.startsWith('video')
  ) {
    return megabytes(MAX_VIDEO_ASSET_SIZE);
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly mp3: RegExp; readonly ogg: RegExp; }'.
  } else if (SUPPORTED_AUDIO_TYPES[type]) {
    return megabytes(
      !isCMSWorkflow() ? MAX_AUDIO_ASSET_SIZE_CANVAS : MAX_AUDIO_ASSET_SIZE
    );
  } else {
    return 0;
  }
};

export const getMaxSize: blobToNumber = (blob) =>
  getMaxFileSize(getNormalizedExtension(blob.name), blob);

const getBlobType: blobToString = weakMemo((blob) =>
  getNormalizedExtension(blob.name)
);

type BuffToHex = (arg1: number, arg2: number, arg3: ArrayBuffer) => string;
export const buffToHex: BuffToHex = (offset, length, buff) => {
  const buffLength = buff.byteLength;
  const safeOffset = clamp(0, buffLength, offset);
  const safeLength = clamp(0, buffLength - safeOffset, length);
  const u8a = new Uint8Array(buff, safeOffset, safeLength);
  const hex: string[] = [];

  u8a.forEach((byte) => {
    hex.push(byteToHex(byte));
  });
  return hex.join('');
};

const hexSignature = (buffer: ArrayBuffer) =>
  buffToHex(0, MAGIC_BYTE_DETECTION_BYTE_LENGTH, buffer);

type ValidateFileType = (
  arg1: RegexMap
) => (arg1: string) => (arg1: ArrayBuffer) => Array<string>;
export const validateFileType: ValidateFileType =
  (validTypes) => (extension: string) => (buffer: ArrayBuffer) => {
    if (!extension) {
      return [`Sorry, we don’t support files without an extension.`];
    }

    if (!validTypes[extension]) {
      return [`Sorry, we don’t support ${toUpper(extension)} files.`];
    }

    if (!validTypes[extension].test(hexSignature(buffer))) {
      return [`Upload failed. Corrupt ${toUpper(extension)} file provided.`];
    }

    return [];
  };

type ValidateFileSize = (arg1: File) => Array<string>;
export const validateFileSize: ValidateFileSize = (blob) => {
  const ext = getBlobType(blob);
  const maxSize = getMaxFileSize(ext, blob);
  return maxSize < blob.size
    ? [
        `Upload failed. Max size for ${toUpper(ext)} files is ${prettySize(
          maxSize
        )}.`,
      ]
    : [];
};

type ValidateBuffer = (
  arg1: RegexMap
) => (arg1: File) => (arg1: ArrayBuffer) => Array<string>;
export const validateBuffer: ValidateBuffer = (typeMap) => {
  const validateType = validateFileType(typeMap);
  return weakMemo((blob) => {
    const validate = validateType(getBlobType(blob));
    return weakMemo((buffer) => {
      const errors = validate(buffer);
      return errors.length ? errors : validateFileSize(blob);
    });
  });
};

export const validateFileBuffer = validateBuffer(supportedExtensions);
export const validateImageBuffer = validateBuffer(SUPPORTED_IMAGE_TYPES);
export const validateVideoBuffer = validateBuffer(SUPPORTED_VIDEO_TYPES);
export const validateAudioBuffer = validateBuffer(SUPPORTED_AUDIO_TYPES);

type fileIsValid = (arg1: File) => (arg1: ArrayBuffer) => boolean;

// Helper function to read the file as text using FileReader and async/await
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;

    reader.readAsText(file);
  });
};

export function isValidLottieJson(parsedJson: Record<any, any>): boolean {
  return (
    // In Point of the Time Ruler. Sets the initial Frame of the animation.
    // Frame rate
    parsedJson != null &&
    // From the lottie animation.json schema, found here:
    // https://github.com/airbnb/lottie-web/blob/master/docs/json/animation.json
    parsedJson.hasOwnProperty('fr') && // frame rate
    parsedJson.hasOwnProperty('layers') && // list of composition layers
    parsedJson.hasOwnProperty('v') && // Bodymovin version
    parsedJson.hasOwnProperty('op') && // "Out Point of the Time Ruler. Sets the final Frame of the animation
    parsedJson.hasOwnProperty('fr') &&
    parsedJson.hasOwnProperty('ip')
  );
}

export const isValidFile: fileIsValid = (blob) => (buffer: ArrayBuffer) =>
  validateFileBuffer(blob)(buffer).length === 0;

export const isValidImage: fileIsValid = (blob) => (buffer: ArrayBuffer) =>
  validateImageBuffer(blob)(buffer).length === 0;

export const isValidVideo: fileIsValid = (blob) => (buffer: ArrayBuffer) =>
  Boolean(blob.type && blob.type.startsWith('video')) &&
  validateVideoBuffer(blob)(buffer).length === 0;

export const isValidAudio: fileIsValid = (blob) => (buffer: ArrayBuffer) =>
  Boolean(blob.type && blob.type.startsWith('audio')) &&
  validateAudioBuffer(blob)(buffer).length === 0;

const valueMapper = (key: assetKey) => (asset: FileRecordType) => {
  // @ts-expect-error - TS2345 - Argument of type 'keyof FileRecord' is not assignable to parameter of type 'keyof FileRecordProps'.
  const value = asset.get(key);
  if (typeof value === 'string') {
    return value.toLowerCase();
  } else {
    return value;
  }
};

type sort = 'asc' | 'desc';
type assetKey = keyof FileRecordType;
type SortAssets = (
  arg1: List<FileRecordType>,
  arg2: assetKey,
  arg3: sort
) => Seq.Indexed<FileRecordType>;
export const sortAssets: SortAssets = (assets, key, order = 'desc') => {
  const sorted = assets.toIndexedSeq().sortBy(valueMapper(key));
  return order === 'asc' ? sorted : sorted.reverse();
};

export const getThumbUrl: assetToString = (asset) =>
  asset.thumbUrl || asset.blobUrl || asset.cdnUrl || asset.s3Url;

type AssetFilter = AssetManagerUIState['filter'];

// This should be a union of all the various meta object shapes
// For now, there's only one shape for the folder filter
type MetaT = null | {
  members: Array<string>;
  name: string;
  versionedResourceId?: string;
};

const FILTER_FUNCTIONS: {
  [key: string]: (
    asset: FileRecordType,
    meta?: MetaT,
    allFolders?: FolderT[]
  ) => boolean;
} = {
  all: () => true,
  images: isImageAsset,
  documents: isDocumentAsset,
  audio: isAudioAsset,
  lottie: isLottieAsset,
  rive: isRiveAsset,
  figma: isFigmaAsset,
  siteAssets: isSiteAsset,
  library: (asset, meta) => {
    return asset.versionedResource === meta?.versionedResourceId;
  },
  folder: (asset, meta) => {
    if (meta && Array.isArray(meta.members)) {
      return meta.members.includes(asset._id);
    }

    return false;
  },
  noFolder: (asset, meta, allFolders = []) => {
    const inFolder = allFolders.some((folder) =>
      folder.members.includes(asset._id)
    );
    if (!meta?.versionedResourceId) {
      // no library id set so we only want site assets
      return !inFolder && !asset.versionedResource;
    } else {
      // filter by specific library
      return !inFolder && asset.versionedResource === meta?.versionedResourceId;
    }
  },
};

const toAssetFilterObject = (
  assetFilter: string | FolderFilter | AssetFilterT
) =>
  typeof assetFilter === 'string'
    ? {type: assetFilter, meta: null}
    : assetFilter;

type FilterAssets = (
  arg1: List<FileRecordType>,
  arg2: AssetFilter,
  arg3?: FolderT[]
) => List<FileRecordType>;
export const filterAssets: FilterAssets = (assets, assetFilter, allFolders) => {
  const {meta, type} = toAssetFilterObject(assetFilter);
  const filterFn = FILTER_FUNCTIONS[type];

  // @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'.
  return assets.filter((asset) => filterFn(asset, meta, allFolders));
};

// ============================================================================
// BLOB TYPE CHECKS - MIGRATED TO @packages/domains/assets/utilities
// Re-exported here for backward compatibility during migration.
// New code should import from @packages/domains/assets/utilities instead.
// ============================================================================
export const isBlobOfType = _isBlobOfType;
export const isImageBlob = _isImageBlob;
export const isVideoBlob = _isVideoBlob;
export const isDocumentBlob = _isDocumentBlob;
export const isJsonBlob = _isJsonBlob;
export const isDotLottieBlob = _isDotLottieBlob;
export const isDotRiveBlob = _isDotRiveBlob;
export const isAudioBlob = _isAudioBlob;
export const isZipBlob = _isZipBlob;

// Is this an asset that we want to display in the Asset Manager?
// We currently show all non-video static and all non read only assets.
export const isVisibleAsset: assetToBoolean = (asset) =>
  Boolean(
    (isSiteAsset(asset) || isLibraryAsset(asset)) &&
    asset.s3Url &&
    !asset.placeholder &&
    !asset.markedAsDeleted &&
    !isVideoAsset(asset) &&
    !isReadOnlyAsset(asset)
  );

const isStaticImage: assetToBoolean = (asset) =>
  Boolean(isVisibleAsset(asset) && isImageAsset(asset));

// Returns all visible assets (assets that are shown and can be used in the site), including ones from installed libraries.
export const getVisibleAssets: assetListToAssetList = weakMemo((assets) =>
  assets.filter(isVisibleAsset)
);

export const getStaticImages: assetListToAssetList = (assets) =>
  assets.filter(isStaticImage);

const S3_OBJECTID_REGEX =
  /^https?:\/\/s3(?:\.[a-z0-9-]+)?\.amazonaws\.com\/(.+?)\/([^/]+)(\/.*)?/i;

// Gets the siteId from static assets or databaseId from dynamic assets
export const getObjectIdFromS3Url: stringToString = (s3Url) =>
  S3_OBJECTID_REGEX.test(s3Url) ? s3Url.replace(S3_OBJECTID_REGEX, '$2') : '';

export const hasDimensions: assetToBoolean = (asset) =>
  typeof asset.width === 'number' && typeof asset.height === 'number';

export const findByFileHash = weakMemo(
  (targetAsset: RawFileType | FileRecordType | FileRecordProps) =>
    weakMemo((assets: AssetsType) => {
      return assets.find((currentAsset) => {
        const isFileHashEqual = targetAsset.fileHash === currentAsset.fileHash;

        const isOriginEqual =
          // both null-ish
          (!targetAsset.origin && !currentAsset.origin) ||
          // both defined and equal
          Boolean(
            targetAsset.origin &&
            currentAsset.origin &&
            namesEqual(targetAsset.origin, currentAsset.origin)
          );

        return isFileHashEqual && isOriginEqual;
      });
    })
);

export const getReifiedXscpFiles = weakMemo(
  // @ts-expect-error - TS7031 - Binding element 'response' implicitly has an 'any' type.
  ({response}): Array<FileRecordType> =>
    response.get('importedFiles').toList().toJS().map(reifyFile)
);

export const getXscpImportedFiles = weakMemo(
  ({type, payload}: {type: string; payload?: unknown}) =>
    type === XSCP_ASSET_IMPORT_SUCCEEDED
      ? getReifiedXscpFiles(payload as {response: Map<string, AssetsType>})
      : emptyArray
);

export const hashFileName: (arg1: FileRecordType) => FileRecordType = weakMemo(
  (asset: FileRecordType) => asset.set('fileHash', getMd5Hash(asset.fileName))
);

type SigningResponse = Map<string, any>;
type SyncMintAsset = (
  arg1: SigningResponse
) => (arg1: FileRecordType) => FileRecordType;
export const syncMintAsset: SyncMintAsset =
  (signingResponse) => (asset: FileRecordType) =>
    asset.merge({
      _id: signingResponse.get('_id'),
      cdnUrl: signingResponse.get('url'),
      createdOn: signingResponse.get('createdOn'),
      s3Url: signingResponse.get('s3Url'),
      markedAsDeleted: false,
      ...(signingResponse.get('origFileName') && {
        origFileName: signingResponse.get('origFileName'),
      }),
    });

type UpdateUploadProgress = (arg1: {
  loaded: number;
  total: number;
}) => (arg1?: FileRecordType) => FileRecordType | undefined;
export const updateUploadProgress: UpdateUploadProgress =
  ({loaded, total}) =>
  (asset?: FileRecordType) =>
    asset?.set(
      'uploadProgress',
      (1 - SIGN_PROGRESS) * (loaded / total) + SIGN_PROGRESS
    ) || asset;

const fitThumbSize = scaleToFit({top: 0, left: 0, width: 128, height: 128});

export const thumbnailDimensions: dimensionsToDimensions = ({
  width,
  height,
}) => {
  const box = fitThumbSize({top: 0, left: 0, width, height});
  return {width: box.width, height: box.height};
};

type DomImage = {
  naturalWidth: number;
  naturalHeight: number;
};

type GetDomImageDimensions = (arg1: DomImage) => Dimensions;
export const getDomImageDimensions: GetDomImageDimensions = (image) => ({
  width: image.naturalWidth,
  height: image.naturalHeight,
});

export const prismCanGenerateThumbnail: stringToBoolean = (url) =>
  COMPAT_THUMB_REGEX.test(url);

export const prismCanMeasureDimensions: stringToBoolean = (url) =>
  COMPAT_DIMENSIONS_REGEX.test(url);

export const getOriginalFileName: stringToString = (url) => {
  const fileName = strRightBack(url, '/');
  return strRight(fileName, '_');
};

export const reifyFile = (json: RawFileType): FileRecordType => {
  return FileRecord.reify(json);
};

// @ts-expect-error - TS7006 - Parameter 'string' implicitly has an 'any' type.
const dotPrefix = (string) => '.' + string;

type BuildInputAcceptAttribute = (arg1: RegexMap) => string;

const buildInputAcceptAttribute: BuildInputAcceptAttribute = (typeMap) =>
  Object.keys(typeMap).map(dotPrefix).join(',');

export const nonVideoAcceptAttribute = () =>
  once(() =>
    buildInputAcceptAttribute({
      ...SUPPORTED_IMAGE_TYPES,
      ...SUPPORTED_DOCUMENT_TYPES,
      ...SUPPORTED_AUDIO_TYPES,
    })
  );

export const imageAcceptAttribute = once(() =>
  buildInputAcceptAttribute(SUPPORTED_IMAGE_TYPES)
);

/**
 * Make sure a file (Map or File Record)'s thumbUrl isn't a data url
 * This is used in DM item updates and AM/DM file uploads to make sure we don't send
 * temporary data: thumbUrls to the server.
 * @param  {Map|Record} file an immutable Map/Record of a file
 * @return {Map|Record}      The same Map/Record passed in with the thumbUrl removed if it was a data: url
 */
export const withoutThumbDataUrl: assetToAsset = (file) => {
  const thumbUrl = file.get('thumbUrl') || '';

  if (thumbUrl.indexOf('data:') === 0) {
    return file.remove('thumbUrl');
  }

  return file;
};

// @ts-expect-error - TS2322 - Type '(url: string) => string | undefined' is not assignable to type 'stringToString'.
export const getFileNameFromUrl: stringToString = (url) => {
  const {pathname} = new URL(url);
  return pathname.split('/').slice(-1)[0];
};

export const hasValidFileNameInUrl: stringToBoolean = (url) => {
  const fileName = getFileNameFromUrl(url);
  const fileExtension = fileName.split('.').slice(-1)[0];
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly jpeg: RegExp; readonly jpg: RegExp; readonly png: RegExp; readonly gif: RegExp; readonly webp: RegExp; readonly bmp: RegExp; readonly svg: RegExp; }'.
  return Boolean(fileExtension && SUPPORTED_IMAGE_TYPES[fileExtension]);
};

/**
 * Builds an array that forms part of a PRISM request.
 * The request will include the parameters for variants we want to generate as well as
 * metadata for already persisted variants needed to determine if smaller responsive variants
 * are compressing at smaller filesizes than their larger counterarts for a particular image.
 * @param  {Object} asset   A static or dynamic asset
 * @return {Array}          An array of variants and/or variant request parameters.
 */
type VariantRequest = {
  w: number;
  q: number;
  persisted?: boolean;
  size?: number;
};
export const buildResponsiveVariantsRequestArray = (
  asset: FileRecordType
): VariantRequest[] => {
  const assetWidth = asset && asset.width;
  const variants =
    asset && asset.variants
      ? asset.variants.reduce<Record<string, any>>((variantMap, variant) => {
          // Narrow to Image Variants
          if ('width' in variant) {
            variantMap[variant.width + '-' + variant.quality] = variant;
          }
          return variantMap;
        }, {})
      : {};

  return VARIANT_WIDTHS.reduce<Array<any>>(
    (
      responsiveVariants: VariantRequest[],
      breakpoint: number
    ): VariantRequest[] => {
      if (assetWidth == null || breakpoint < assetWidth) {
        const mapKey = breakpoint + '-100';
        const variant = variants[mapKey];

        const variantRequest = {
          w: breakpoint,
          q: 100,
        } as const;

        return responsiveVariants.concat(
          variant
            ? {
                ...variantRequest,
                persisted: true,
                size: variant.size,
              }
            : variantRequest
        );
      }

      return responsiveVariants;
    },
    []
  );
};

export const isFileUploading = (value?: ImageRefType | null) => {
  if (value) {
    // CMS Images are stored as a Map, so we can't check for the tmp property with dot notation
    if (Map.isMap(value)) {
      return (
        value.get('tmp') instanceof ImageRefRecord &&
        typeof value.getIn(['tmp', 'file', 'uploadProgress']) === 'number'
      );
    }

    return (
      (value instanceof ImageRefRecord ||
        // @ts-expect-error - TS2339 - Property 'tmp' does not exist on type 'never'.
        value.tmp instanceof ImageRefRecord) &&
      typeof value.getIn(['tmp', 'file', 'uploadProgress']) === 'number'
    );
  }

  return false;
};

export const isPluginAsset = (assetId: string) =>
  assetId.startsWith('plugins/');

/**
 * Puts assets into 1 of 2 buckets so we can later determine what upload flow to use.
 */
export const sortFilesByPlanType = (blobs: File[]) => {
  const {freePlanFiles, paidPlanFiles} = Array.from(blobs).reduce(
    (result, file) => {
      if (
        (isDocumentBlob(file) &&
          !isJsonBlob(file) &&
          !isDotLottieBlob(file) &&
          !isDotRiveBlob(file)) ||
        isAudioBlob(file)
      ) {
        // @ts-expect-error - TS2345 - Argument of type 'File' is not assignable to parameter of type 'never'.
        result.paidPlanFiles.push(file);
      } else {
        // @ts-expect-error - TS2345 - Argument of type 'File' is not assignable to parameter of type 'never'.
        result.freePlanFiles.push(file);
      }

      return result;
    },
    {
      freePlanFiles: [],
      paidPlanFiles: [],
    }
  );

  return {freePlanFiles, paidPlanFiles};
};

/**
 * Determines the file type that the user is trying to upload based on the selected files.
 * Used to determine which upload flow to use.
 */
export const getSiteFileTypeForUpload = ({
  blobs,
  allowDocumentUploads,
}: {
  blobs: Array<File>;
  allowDocumentUploads: boolean;
}): 'FILE' | 'IMAGE' => {
  const {paidPlanFiles} = sortFilesByPlanType(blobs);

  // Users can upload images and Lottie (json) files on a free plan
  const canUploadPremiumFiles = allowDocumentUploads;
  const selectedPremiumFilesForUpload = paidPlanFiles.length > 0;
  return canUploadPremiumFiles || !selectedPremiumFilesForUpload
    ? 'FILE'
    : 'IMAGE';
};

export type AssetHelperFunctions = {
  isSigned: (arg1: {
    type: string;
    asset: FileRecordType;
    mintId: string;
    fileHash?: string;
    siteId?: string;
    databaseId?: string;
  }) => boolean;
  isDone: (arg1: Record<any, any>, arg2: string, arg3: string) => boolean;
  getDoneAsset: (
    arg1: string,
    assetStore: AssetState
  ) => FileRecordType | null | undefined;
  isFailed: (arg1: Record<any, any>, arg2: string, arg3: string) => boolean;
};

type AssetUploadHelpers = {
  imageSelectedForUpload: AssetHelperFunctions;
  documentSelectedForUpload: AssetHelperFunctions;
  jsonFileSelectedForUpload: AssetHelperFunctions;
  riveFileSelectedForUpload: AssetHelperFunctions;
};

/**
 * A set of helpers to be used by the various types of assets that can be uploaded.
 * Useful if you need to know when a file has been signed, uploaded, or failed.
 */
export const assetUploadHelpers: AssetUploadHelpers = {
  imageSelectedForUpload: {
    isSigned: ({type, asset, fileHash, siteId, databaseId}) => {
      return (
        type === ASSET_SIGNING_FINISHED &&
        // We are checking here with the file hash because if multiple identical
        // assets are tried to be added at the same time, only the first one is
        // actually being uploaded. We still want to use the first asset's
        // upload progress status with the rest of the identical files so we need
        // to check if the file hash matches.
        asset.fileHash === fileHash &&
        // In addition to the matching file hash, we want to match only
        // assets that belong to the same bucket (site or database).
        (siteId ? asset.siteId === siteId : asset.databaseId === databaseId)
      );
    },
    isDone: ({type, payload, meta}, mintId, realId) => {
      return (
        (type === ASSET_UPLOAD_FINISHED && meta === realId) ||
        (type === ASSET_S3_UPLOAD_NOT_NEEDED &&
          payload.meta.signingResponse.get('_id') === realId)
      );
    },
    getDoneAsset: (realId, assetStore) => {
      // Note: It is important that we read AssetStore state with `flux.getStoreState`
      // because we must rely on the state that is being still updated on ASSET_UPLOAD_FINISHED.
      const {assets} = assetStore;
      return assets.get(realId);
    },
    isFailed: ({type, payload, meta}, mintId, realId) =>
      (type === IMAGE_READ_FOR_VALIDATION_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_READ_FOR_HASHING_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_HASHING_FAILED && payload.meta.asset._id === mintId) ||
      (type === IMAGE_ASSET_LOADING_FAILED && payload.meta._id === mintId) ||
      (type === IMAGE_ASSET_THUMBNAIL_FAILED && payload.meta._id === mintId) ||
      (type === ASSET_SIGNING_FAILED && meta._id === mintId) ||
      (type === ASSET_UPLOAD_FAILED && meta === realId) ||
      (type === ASSET_REPLACE_UPLOAD_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === ASSET_REPLACE_PERSIST_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      // Note: validateImageBuffer is weakMemoed and also used in
      // NotificationStore[IMAGE_READ_FOR_VALIDATION_FINISHED]
      (type === IMAGE_READ_FOR_VALIDATION_FINISHED &&
        payload.meta.asset._id === mintId &&
        validateImageBuffer(payload.input.blob)(payload.result).length > 0),
  },
  documentSelectedForUpload: {
    isSigned: ({type, asset, fileHash, siteId, databaseId}) => {
      return (
        type === ASSET_SIGNING_FINISHED &&
        // We are checking here with the file hash because if multiple identical
        // assets are tried to be added at the same time, only the first one is
        // actually being uploaded. We still want to use the first asset's
        // upload progress status with the rest of the identical files so we need
        // to check if the file hash matches.
        asset.fileHash === fileHash &&
        // In addition to the matching file hash, we want to match only
        // assets that belong to the same bucket (site or database).
        (siteId ? asset.siteId === siteId : asset.databaseId === databaseId)
      );
    },
    isDone: ({type, payload, meta}, mintId, realId) => {
      return (
        (type === ASSET_UPLOAD_FINISHED && meta === realId) ||
        (type === ASSET_S3_UPLOAD_NOT_NEEDED &&
          payload.meta.signingResponse.get('_id') === realId)
      );
    },
    getDoneAsset: (realId, assetStore) => {
      // Note: It is important that we read AssetStore state with `flux.getStoreState`
      // because we must rely on the state that is being still updated on ASSET_UPLOAD_FINISHED.
      const {assets} = assetStore;
      return assets.get(realId);
    },
    isFailed: ({type, payload, meta}, mintId, realId) =>
      (type === FILE_READ_FOR_VALIDATION_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_READ_FOR_HASHING_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_HASHING_FAILED && payload.meta.asset._id === mintId) ||
      (type === ASSET_SIGNING_FAILED && meta._id === mintId) ||
      (type === ASSET_UPLOAD_FAILED && meta === realId) ||
      (type === ASSET_REPLACE_UPLOAD_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === ASSET_REPLACE_PERSIST_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === FILE_READ_FOR_VALIDATION_FINISHED &&
        payload.meta.asset._id === mintId &&
        validateFileBuffer(payload.input.blob)(payload.result).length > 0),
  },
  jsonFileSelectedForUpload: {
    isSigned: ({type, asset, fileHash, siteId, databaseId}) => {
      return (
        type === ASSET_SIGNING_FINISHED &&
        // We are checking here with the file hash because if multiple identical
        // assets are tried to be added at the same time, only the first one is
        // actually being uploaded. We still want to use the first asset's
        // upload progress status with the rest of the identical files so we need
        // to check if the file hash matches.
        asset.fileHash === fileHash &&
        // In addition to the matching file hash, we want to match only
        // assets that belong to the same bucket (site or database).
        (siteId ? asset.siteId === siteId : asset.databaseId === databaseId)
      );
    },
    isDone: ({type, payload, meta}, mintId, realId) => {
      return (
        (type === ASSET_UPLOAD_FINISHED && meta === realId) ||
        (type === ASSET_S3_UPLOAD_NOT_NEEDED &&
          payload.meta.signingResponse.get('_id') === realId)
      );
    },
    getDoneAsset: (realId, assetStore) => {
      // Note: It is important that we read AssetStore state with `flux.getStoreState`
      // because we must rely on the state that is being still updated on ASSET_UPLOAD_FINISHED.
      const {assets} = assetStore;
      return assets.get(realId);
    },
    isFailed: ({type, payload, meta}, mintId, realId) =>
      (type === JSON_FILE_READ_FOR_VALIDATION_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_READ_FOR_HASHING_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_HASHING_FAILED && payload.meta.asset._id === mintId) ||
      (type === ASSET_SIGNING_FAILED && meta._id === mintId) ||
      (type === ASSET_REPLACE_UPLOAD_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === ASSET_REPLACE_PERSIST_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === ASSET_UPLOAD_FAILED &&
        meta === realId &&
        type === JSON_FILE_READ_FOR_VALIDATION_FINISHED &&
        payload.meta.asset._id === mintId &&
        validateJsonFile(payload.result).length > 0),
  },
  riveFileSelectedForUpload: {
    isSigned: ({type, asset, fileHash, siteId, databaseId}) => {
      return (
        type === ASSET_SIGNING_FINISHED &&
        // We are checking here with the file hash because if multiple identical
        // assets are tried to be added at the same time, only the first one is
        // actually being uploaded. We still want to use the first asset's
        // upload progress status with the rest of the identical files so we need
        // to check if the file hash matches.
        asset.fileHash === fileHash &&
        // In addition to the matching file hash, we want to match only
        // assets that belong to the same bucket (site or database).
        (siteId ? asset.siteId === siteId : asset.databaseId === databaseId)
      );
    },
    isDone: ({type, payload, meta}, mintId, realId) => {
      return (
        (type === ASSET_UPLOAD_FINISHED && meta === realId) ||
        (type === ASSET_S3_UPLOAD_NOT_NEEDED &&
          payload.meta.signingResponse.get('_id') === realId)
      );
    },
    getDoneAsset: (realId, assetStore) => {
      // Note: It is important that we read AssetStore state with `flux.getStoreState`
      // because we must rely on the state that is being still updated on ASSET_UPLOAD_FINISHED.
      const {assets} = assetStore;
      return assets.get(realId);
    },
    isFailed: ({type, payload, meta}, mintId, realId) =>
      (type === RIVE_FILE_READ_FOR_VALIDATION_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_READ_FOR_HASHING_FAILED &&
        payload.meta.asset._id === mintId) ||
      (type === FILE_HASHING_FAILED && payload.meta.asset._id === mintId) ||
      (type === ASSET_SIGNING_FAILED && meta._id === mintId) ||
      (type === ASSET_UPLOAD_FAILED && meta === realId) ||
      (type === ASSET_REPLACE_UPLOAD_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === ASSET_REPLACE_PERSIST_FAILED &&
        isImmutable(meta) &&
        meta?.get('asset')?._id === realId) ||
      (type === RIVE_FILE_READ_FOR_VALIDATION_FINISHED &&
        payload.meta.asset._id === mintId &&
        validateFileBuffer(payload.input.blob)(payload.result).length > 0),
  },
};

export const validateJsonFile = (
  string: string,
  isFromWellKnownFolder?: boolean | null
): Array<
  | string
  | {
      message: string;
      link: string;
      callToActionText: string;
      title: string;
    }
> => {
  try {
    const animationData = JSON.parse(string);

    if (!isValidLottieJson(animationData) && !isFromWellKnownFolder) {
      return [
        "This JSON file isn't a Lottie animation. Check the Lottie documentation for details.",
      ];
    }

    if (
      isValidLottieJson(animationData) &&
      lottieContainsUnsupportedFeatures(animationData)
    ) {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'LottieJSONUploadFailed',
        'Could not upload lottie json because it contains an unsupported feature.',
        {
          json: string,
        }
      );

      return [
        {
          title: 'Unsupported JSON file',
          message:
            'This JSON file includes unsupported elements. Check the Lottie documentation for details.',
          callToActionText: 'Show me',
          link: 'https://help.webflow.com/hc/en-us/articles/33961351058835-Embed-Lottie-animations-in-Classic-Interactions',
        },
      ];
    }

    return [];
  } catch (e: any) {
    return [
      `Upload failed. Corrupt JSON file provided. Make sure the contents are valid JSON.`,
    ];
  }
};

export const lottieContainsUnsupportedFeatures = (
  parsedJson: Record<any, any>
) => {
  const container = window.document.createElement('div');
  try {
    // HACK: there's no way for us to determine which unsupported feature is
    // being used in lottie, so we do a catch all: if loading the animation
    // succeeds, then we know the lottie JSON will work.
    const instance = lottie.loadAnimation({
      container,
      // Autoplay automatically triggers the lottie animation playback once the
      // instance is loaded. If the JSON contains bad data, then the playback
      // will throw here.
      //
      // There still isn't a more elegant API from the lib to help detect
      // whether the json contains unsupported features, so I'm approaching it
      // via throw/catch to disallow these assets from being uploaded.
      autoplay: true,
      loop: false,
      renderer: 'svg',
      animationData: parsedJson,
    });

    // Make sure we clean up to avoid memory leaks
    instance.destroy();
    container.remove();
    return false;
  } catch (e: any) {
    container.remove();
    return true;
  }
};

export const getExistingAssetAfterFileHashing = (
  action: FileHashingFinishedAction,
  realId: string | undefined
) => {
  // When the action `FILE_HASHING_FINISHED` is fired, we need to check if the asset that
  // was just hashed is already stored in the assets. If the asset is already stored in
  // the assets, we can use its upload progress status by assigning its ID to `realId`.
  const existingAsset = findExistingIdenticalAsset(
    action.payload.meta.asset.set('fileHash', action.payload.result)
  )(action.state.AssetStore.assets);

  // If the existing asset has still a mint ID, we can't use it so we need to wait
  // until the asset has been signed to get the real ID.
  if (existingAsset && !isMintId(existingAsset._id)) {
    realId = existingAsset._id;
  }

  return realId;
};
